import type {
  TemplateDetails,
  TemplateDetailsResponse,
  TemplateListItem,
  TemplateListResponse,
  TemplateSlot,
  TemplateSlotResponse,
} from "@/features/calendar/types/templates";

import { create } from "zustand";

import { API_ENDPOINTS } from "@/constants/api";
import { axiosApiInstance } from "@/libs/axios";

type TemplatesStore = {
  // Templates list state
  templates: TemplateListItem[];
  isLoadingTemplates: boolean;
  templatesError: string | null;

  // Current template details state
  currentTemplate: TemplateDetails | null;
  isLoadingTemplate: boolean;
  templateError: string | null;

  // Template slots state
  templateSlots: TemplateSlot[];
  isLoadingSlots: boolean;
  slotsError: string | null;

  // Actions
  fetchTemplates: () => Promise<void>;
  fetchTemplateDetails: (templateId: string) => Promise<void>;
  fetchTemplateSlots: (templateId: string) => Promise<void>;

  // Setters
  setTemplates: (templates: TemplateListItem[]) => void;
  setCurrentTemplate: (template: TemplateDetails | null) => void;
  setTemplateSlots: (slots: TemplateSlot[]) => void;

  // Reset functions
  resetTemplateState: () => void;
  resetErrors: () => void;
};

export const useTemplatesStore = create<TemplatesStore>((set, get) => ({
  // Templates list state
  templates: [],
  isLoadingTemplates: false,
  templatesError: null,

  // Current template details state
  currentTemplate: null,
  isLoadingTemplate: false,
  templateError: null,

  // Template slots state
  templateSlots: [],
  isLoadingSlots: false,
  slotsError: null,

  // Actions
  fetchTemplates: async () => {
    try {
      set({ isLoadingTemplates: true, templatesError: null });
      const { data } = await axiosApiInstance.get<TemplateListResponse>(
        API_ENDPOINTS.TUTOR.TEMPLATES.LIST,
      );

      set({ templates: data.data });
    } catch (error: any) {
      console.error("Failed to fetch templates:", error);
      set({
        templatesError: error.response?.data?.message || "Failed to fetch templates",
      });
    } finally {
      set({ isLoadingTemplates: false });
    }
  },

  fetchTemplateDetails: async (templateId: string) => {
    try {
      set({ isLoadingTemplate: true, templateError: null });
      const { data } = await axiosApiInstance.get<TemplateDetailsResponse>(
        `${API_ENDPOINTS.TUTOR.TEMPLATES.LIST}/${templateId}`,
      );

      set({ currentTemplate: data.data });

      // After fetching template details, also fetch its slots
      get().fetchTemplateSlots(templateId);
    } catch (error: any) {
      console.error("Failed to fetch template details:", error);
      set({
        templateError: error.response?.data?.message || "Failed to fetch template details",
      });
    } finally {
      set({ isLoadingTemplate: false });
    }
  },

  fetchTemplateSlots: async (templateId: string) => {
    try {
      set({ isLoadingSlots: true, slotsError: null });
      const { data } = await axiosApiInstance.get<TemplateSlotResponse>(
        `${API_ENDPOINTS.TUTOR.TEMPLATES.LIST}/${templateId}/slots`,
      );

      set({ templateSlots: data.data });
    } catch (error: any) {
      console.error("Failed to fetch template slots:", error);
      set({
        slotsError: error.response?.data?.message || "Failed to fetch template slots",
      });
    } finally {
      set({ isLoadingSlots: false });
    }
  },

  // Setters
  setTemplates: (templates: TemplateListItem[]) => set({ templates }),
  setCurrentTemplate: (template: TemplateDetails | null) => set({ currentTemplate: template }),
  setTemplateSlots: (slots: TemplateSlot[]) => set({ templateSlots: slots }),

  // Reset functions
  resetTemplateState: () =>
    set({
      currentTemplate: null,
      templateSlots: [],
      templateError: null,
      slotsError: null,
    }),

  resetErrors: () =>
    set({
      templatesError: null,
      templateError: null,
      slotsError: null,
    }),
}));
